import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, FileAltIcon } from 'components/icons';
import { cn } from 'utils';
import { JobsiteOverviewSection } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewSection';
import {
  JobsiteOverviewEmptyState,
  Shimmer,
} from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewStates';
import { GetJobsiteOverviewDataQuery } from 'apollo/generated/client-operations';

export type JobsiteOverviewLogsSectionSubmission =
  GetJobsiteOverviewDataQuery['getJobsite']['jobsiteFormSubmissions']['edges'][number];

export type JobsiteOverviewLogsSectionProps = {
  submissions: JobsiteOverviewLogsSectionSubmission[];
  linkQueryString: string;
  loading?: boolean;
};

const LOGS_GRID_CLASSES = cn(
  'odin-grid odin-grid-cols-2 md:odin-grid-cols-3',
  'lg:odin-grid-cols-5 2xl:odin-grid-cols-6 odin-gap-3',
);

const SKELETON_CARD_COUNT = 5;

function LogCardSkeleton(): ReactElement {
  return (
    <div
      className={cn(
        'odin-flex odin-flex-col odin-gap-3',
        'odin-rounded-2xl odin-border odin-border-odin-medium-gray',
        'odin-bg-odin-white odin-p-4',
      )}
    >
      <Shimmer className="odin-h-4 odin-w-2/3" />
      <Shimmer className="odin-h-6 odin-w-8" />
    </div>
  );
}

type LogCardProps = {
  formKey: string;
  formName: string;
  count: number;
  linkTo: string;
};

function LogCard({
  formKey,
  formName,
  count,
  linkTo,
}: LogCardProps): ReactElement {
  return (
    <Link
      key={formKey}
      to={linkTo}
      className={cn(
        'odin-group',
        'odin-flex odin-flex-col odin-gap-3',
        'odin-rounded-2xl odin-border odin-border-odin-medium-gray',
        'odin-bg-odin-white odin-p-4',
        'odin-min-w-0',
        'odin-transition-all odin-duration-200 hover:odin-border-odin-dark-gray',
      )}
    >
      <div className="odin-flex odin-items-center odin-justify-between odin-gap-2">
        <span
          className="odin-text-sm odin-font-medium odin-text-odin-navy odin-truncate"
          title={formName}
        >
          {formName}
        </span>
        <ChevronRightIcon className="odin-w-3 odin-h-3 odin-shrink-0 odin-text-odin-navy" />
      </div>
      <div className="odin-flex odin-flex-col">
        <span className="odin-text-lg odin-font-semibold odin-text-odin-navy">
          {count}
        </span>
        <span className="odin-text-sm odin-text-odin-navy">Total</span>
      </div>
    </Link>
  );
}

export function JobsiteOverviewLogsSection({
  submissions,
  linkQueryString,
  loading = false,
}: JobsiteOverviewLogsSectionProps): ReactElement {
  const cards = React.useMemo(() => {
    const countsByFormId = new Map<
      string,
      { key: string; name: string; count: number }
    >();
    submissions.forEach(({ node }) => {
      const { id, key, name } = node.jobsiteForm.form;
      const existing = countsByFormId.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        countsByFormId.set(id, { key, name, count: 1 });
      }
    });
    return Array.from(countsByFormId.entries())
      .map(([id, value]) => ({ id, ...value }))
      .sort((a, b) => b.count - a.count);
  }, [submissions]);

  if (loading) {
    return (
      <JobsiteOverviewSection label="Logs & documentation">
        <div className={LOGS_GRID_CLASSES}>
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <LogCardSkeleton key={i} />
          ))}
        </div>
      </JobsiteOverviewSection>
    );
  }

  return (
    <JobsiteOverviewSection label="Logs & documentation">
      {cards.length === 0 ? (
        <JobsiteOverviewEmptyState
          icon={FileAltIcon}
          message="No logs filed on this date."
          className="odin-py-6"
        />
      ) : (
        <div className={LOGS_GRID_CLASSES}>
          {cards.map(({ id, key, name, count }) => (
            <LogCard
              key={id}
              formKey={key}
              formName={name}
              count={count}
              linkTo={`/forms/${key}?${linkQueryString}`}
            />
          ))}
        </div>
      )}
    </JobsiteOverviewSection>
  );
}
